/**
 * Registering the service worker that makes sonolume installable.
 *
 * # What this is for, and what it is not for
 *
 * A phone will only offer *"add to home screen"* for a page with a manifest,
 * icons **and** a registered worker that handles `fetch`. `public/sw.js` is
 * that worker and it caches nothing at all: this is a live party game whose
 * state arrives over a socket, and the one thing a cache would reliably deliver
 * is a stale bundle — a phone running last week's client against this week's
 * server, which reads as a server fault and takes a day to recognise.
 *
 * So the only thing installability buys here is the *frame*: a home-screen
 * icon, no address bar, the theme colour on the status bar. That is worth
 * having on the one screen every player uses, and it is worth nothing else.
 *
 * # Three deliberate refusals
 *
 * - **Only on a served page.** A packaged television is loaded from a file, has
 *   no origin, and cannot register a worker; `serverOrigin.ts` already knows the
 *   difference and this asks it rather than guessing.
 * - **Only in a production build.** A worker that survives a dev-server restart
 *   is how "I changed the code and nothing happened" starts.
 * - **Never blocking, never reported.** A browser that refuses (private mode, an
 *   old iOS, a policy) still runs the game perfectly. There is nothing for a
 *   player to do about it, so there is nothing to tell them.
 */

import { isServedPage } from "./serverOrigin.js";

/** Register the worker, if this page is one that can have one. */
export function makeInstallable(): void {
  if (!import.meta.env.PROD) {
    return;
  }
  if (!isServedPage() || !("serviceWorker" in navigator)) {
    return;
  }
  // After `load`, so registration never competes with the first render for the
  // network on a phone that is about to join a party.
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => {
      // Nothing a player can act on. The game does not depend on it.
    });
  });
}
